<template>
  <v-dialog v-model="isOpen" width="800" persistent>
    <template v-slot:activator="activatorProps">
      <slot name="activator" v-bind="activatorProps" />
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('post_advance') }}</span>
      </v-card-title>
      <v-card-text class="scrollable-content">
        <CustomForm
          ref="form"
          v-model="advance"
          :default-fields="defaultFields"
          :possible-fields="getPossibleFields()"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="close">{{ $t('Cancel') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!isValid"
          :loading="loading"
          raised
          data-testid="submit-ris-advance"
          @click="postAdvance"
        >
          {{ $t('post_advance') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import Committee from '@/store/models/committee'
import User from '@/store/models/user'
import isEmpty from 'lodash/isEmpty'
import Profile from '@/store/models/profile'
import get from 'lodash/get'

export default {
  name: 'RisAdvance',
  props: {
    committee: {
      type: Committee,
      required: true,
    },
  },
  data() {
    return {
      businessTypes: [],
      committees: [],
      defaultFields: ['attachments', 'business_type', 'has_signature_sheet', 'is_urgent', 'title', 'wording'],
      advance: { files: [] },
      isOpen: false,
      loading: false,
      rightsHolderOptions: [
        { text: this.$t('Business_case_advance_creator'), value: 'advance_creator' },
        { text: this.$t('Business_case_faction'), value: 'faction' },
        { text: this.$t('Business_case_commission'), value: 'commission' },
        { text: this.$t('Business_case_cross_party'), value: 'cross_party' },
      ],
    }
  },
  computed: {
    committeeMembers() {
      const users = User.all().filter((user) => user.roles.includes('member'))
      return users.map((user) => ({
        text: [user.first_name, user.last_name].join(' ') + ` (${user.username})`,
        type: 'user',
        value: user.username,
      }))
    },
    submitters() {
      return [...this.committees, ...this.committeeMembers]
    },
    user() {
      return Profile.query().first()
    },
    isValid() {
      const descriptionValid = !isEmpty(this.advance.wording) || this.$refs.form?.hasAttachments
      const businessType = this.advance.business_type !== null
      return this.$refs.form?.valid && ((descriptionValid && businessType) || this.$refs.form?.customForm)
    },
  },
  async mounted() {
    await this.fetchBusinessTypes()
    await this.fetchCommittees()
  },
  methods: {
    isEmpty,
    ...mapActions({
      reset: 'risadvance/reset',
    }),
    close() {
      this.isOpen = false
      this.$refs.form.cleanup()
      this.advance = { files: [] }
      this.loading = false
    },
    async fetchBusinessTypes() {
      const businessTypes = await api.get(createLink('businesstypes'), { params: { show_for_advance: true } })
      this.businessTypes = businessTypes.data.map((businessType) => ({
        text: businessType.title,
        value: businessType.id,
      }))
    },
    async fetchCommittees() {
      const { data: committees } = await api.get(createLink('committees/listing'))
      this.committees = committees.map((committee) => ({
        text: committee.title,
        type: 'committee',
        value: committee.api_uid,
      }))
    },
    async postAdvance() {
      if (!this.isValid) {
        return
      }

      this.loading = true
      try {
        const formData = this.$refs.form.getFormData(this.advance)
        const response = await api.post(createLink(`/ris/post_advance/${this.committee.id}`), formData)

        if (response.status !== 201) {
          const error = new Error(this.$t('ris_submission_unexpected_response'))
          error.type = 'WAFException'
          throw error
        }

        this.$emit('posted', response.data)
        this.reset()
        this.close()
      } catch (error) {
        const nonFieldErrors = get(error, 'response.data.non_field_errors', []).join(' ')
        const messageDetail = get(error, 'response.data.detail', null)
        const messageText = get(error, 'response.data.text', null)
        const messageToDisplay =
          messageText || messageDetail || nonFieldErrors || error.message || this.$t('unknown_error')

        this.notifyError(this.$t('SubmitAdvanceCardSubmitError', { message: messageToDisplay }))
        this.loading = false
        throw error
      }
    },
    getPossibleFields() {
      return {
        business_type: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'business_type',
          items: this.businessTypes,
          label: this.$t('Business_type'),
          required: true,
        },
        title: {
          component: 'v-text-field',
          identifier: 'title',
          label: this.$t('Business_case_title'),
          required: true,
        },
        wording: {
          component: 'v-textarea',
          identifier: 'wording',
          label: this.$t('Wording'),
        },
        reason: {
          component: 'v-textarea',
          identifier: 'reason',
          label: this.$t('Business_case_reason'),
        },
        advance_creator: {
          component: 'v-autocomplete',
          default: this.user?.username,
          disabled: !this.user?.can_create_advances_for_other_people,
          identifier: 'advance_creator',
          items: this.committeeMembers,
          label: this.$t('Business_case_advance_creator'),
          required: true,
        },
        second_signature: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'second_signature',
          items: this.committeeMembers,
          label: this.$t('Business_case_second_signature'),
        },
        third_signature: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'third_signature',
          items: this.committeeMembers,
          label: this.$t('Business_case_third_signature'),
        },
        submitters: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'submitters',
          items: this.submitters,
          label: this.$t('Business_case_submitters'),
          multiple: true,
          returnObject: true,
        },
        parliamentarians: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'parliamentarians',
          items: this.committeeMembers,
          label: this.$t('Business_case_parliamentarians'),
          multiple: true,
        },
        coalitions: {
          clearable: true,
          component: 'v-autocomplete',
          identifier: 'coalitions',
          items: this.committees,
          label: this.$t('Business_case_coalitions'),
          multiple: true,
        },
        rights_holder: {
          clearable: true,
          component: 'v-select',
          identifier: 'rights_holder',
          items: this.rightsHolderOptions,
          label: this.$t('Business_case_rights_holder'),
        },
        is_urgent: {
          component: 'v-checkbox',
          default: true,
          identifier: 'is_urgent',
          label: this.$t('mark_urgent'),
        },
        has_signature_sheet: {
          component: 'v-checkbox',
          default: true,
          identifier: 'has_signature_sheet',
          label: this.$t('mark_signature_sheet'),
        },
        attachments: {
          component: 'FileInput',
          identifier: 'attachments',
          label: this.$t('advance_form_attachment_label'),
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-select-button {
  position: relative;
  overflow: hidden;
  margin: 10px 0 10px 10px;
}
.file-upload-file-input {
  display: none;
}
.scrollable-content {
  height: 40vh;
  overflow-y: auto;
}
</style>
